export type Habitat = 'forest' | 'savanna' | 'desert' | 'ocean' | 'mountain' | 'arctic' | 'freshwater' | 'domestic';
export type Diet = 'carnivore' | 'herbivore' | 'omnivore';
export type Klass = 'mammal' | 'bird' | 'reptile' | 'fish' | 'amphibian' | 'insect' | 'arachnid' | 'mollusk' | 'crustacean';
export type Size = 'small' | 'medium' | 'large';

export interface Animal {
  id: string;
  name: string;
  aliases: string[];
  klass: Klass;
  habitat: Habitat;
  diet: Diet;
  size: Size;
  continents: string[];
  legs: number;
  clue: string;
}

export interface AnimalsData {
  animals: Animal[];
}

export interface ScoreDetails {
  klass: boolean;
  habitat: boolean;
  diet: boolean;
  size: boolean;
  continents: boolean;
  legs: boolean;
}

export type AnimalHint = keyof ScoreDetails | 'clue';

export interface AnimalsState {
  targetId: string;
  guesses: string[];
  scoreByGuess: Record<string, { score: number; max: number; details: ScoreDetails }>;
  hintsRevealed: AnimalHint[];
  daily: boolean;
  surrendered?: boolean;
}

export interface AnimalsGuess {
  id: string;
  name: string;
}

export const HINT_UNLOCK_ATTEMPTS = 3;
export const HINT_ORDER: AnimalHint[] = ['klass', 'habitat', 'diet', 'size', 'continents', 'legs', 'clue'];

export function normalizeAnimalInput(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function searchLabels(animal: Animal): string[] {
  return [animal.name, ...animal.aliases].filter((label) => label.trim().length > 0);
}

export function findAnimal(input: string, list: Animal[]): Animal | null {
  const query = normalizeAnimalInput(input);
  if (!query) return null;

  for (const animal of list) {
    if (searchLabels(animal).some((label) => normalizeAnimalInput(label) === query)) {
      return animal;
    }
  }

  const prefixMatches = new Map<string, Animal>();
  for (const animal of list) {
    if (searchLabels(animal).some((label) => normalizeAnimalInput(label).startsWith(query))) {
      prefixMatches.set(animal.id, animal);
    }
  }

  return prefixMatches.size === 1 ? [...prefixMatches.values()][0] : null;
}

export function scoreVsTarget(
  guessed: Animal,
  target: Animal,
): { score: number; max: number; details: ScoreDetails } {
  const details: ScoreDetails = {
    klass: guessed.klass === target.klass,
    habitat: guessed.habitat === target.habitat,
    diet: guessed.diet === target.diet,
    size: guessed.size === target.size,
    continents: guessed.continents.some((continent) => target.continents.includes(continent)),
    legs: guessed.legs === target.legs,
  };
  const score = Object.values(details).filter(Boolean).length;
  return { score, max: 6, details };
}

export function availableHints(target: Animal): AnimalHint[] {
  return HINT_ORDER.filter((hint) => hint !== 'clue' || target.clue.trim().length > 0);
}

export function attemptsUntilHint(guessCount: number): number {
  return Math.max(0, HINT_UNLOCK_ATTEMPTS - guessCount);
}

export function nextAnimalHint(
  target: Animal,
  revealed: readonly AnimalHint[],
  guessCount: number,
): AnimalHint | null {
  if (attemptsUntilHint(guessCount) > 0) return null;
  return availableHints(target).find((hint) => !revealed.includes(hint)) ?? null;
}

export function autocompleteValues(list: Animal[]): string[] {
  const values = new Map<string, string>();
  for (const animal of list) {
    for (const label of searchLabels(animal)) {
      const normalized = normalizeAnimalInput(label);
      if (!normalized) continue;
      values.set(normalized, label);
    }
  }
  return [...values.values()].sort((a, b) => a.localeCompare(b, 'it'));
}